import { Link } from 'react-router-dom';
import { Box, Divider, ListItem, capitalize as capitalizeUtil } from '@mui/material';
import dayjs from 'dayjs';
import * as utils from '../../utils';
import { mediumDate } from '/src/lib/constants';
import { URLString } from '/src/lib/types';

export type InfoItemProps = {
  /**
   * Item title
   */
  title?: string;
  /**
   * Value to display
   */
  value?: string | number | Date | React.ReactNode;
  /**
   * Flex amount for title
   */
  titleFlex?: string;
  /**
   * Icon to display in title
   */
  titleIcon?: React.ReactNode;
  /**
   * If set, the displayed value is a link to the given URL
   */
  url?: URLString;
  /**
   * Formats value as a dollar amount
   */
  currency?: boolean;
  /**
   * Formats value as a percentage
   */
  percent?: boolean;
  /**
   * Formats value as a date
   */
  date?: boolean;
  /**
   * Bolds the displayed value
   */
  bold?: boolean;
  /**
   * Format to convert date to. Used when `date` is true
   */
  dateFormat?: string;
  /**
   * Whether to display the component
   */
  show?: boolean;
  /**
   * Whether to show a Divider component above this one. Used when InfoItems are in a list
   */
  divider?: boolean;
  /**
   * Displays text in a smaller font
   */
  smallFont?: boolean;
  /**
   * Capitalizes component text
   */
  capitalize?: boolean;
  /**
   * Info item row height
   */
  rowHeight?: number;
};

export const InfoItem: React.FC<InfoItemProps> = ({
  title,
  value,
  titleFlex,
  titleIcon,
  url,
  currency = false,
  percent = false,
  date = false,
  bold = false,
  dateFormat = mediumDate,
  show = true,
  divider = false,
  smallFont = false,
  capitalize = false,
  rowHeight = 48,
}) => {
  const displayValue = (): React.ReactNode => {
    let val: React.ReactNode = '';

    if (typeof value === 'object') {
      if (date) val = dayjs(value as Date).format(dateFormat);
      else val = value as React.ReactNode;
    } else if (typeof value === 'number') {
      const numValue = isNaN(value) ? 0 : value;
      if (percent) val = utils.percent.format(numValue);
      else if (currency) val = utils.currency.format(numValue);
    } else if (typeof value === 'string') {
      val = capitalize ? capitalizeUtil(value) : value;
    }

    if (url) val = <Link to={url}>{val}</Link>;

    return val;
  };

  return divider ? (
    <Divider />
  ) : (
    show && (
      <ListItem className="pad-top-bottom-xs" sx={{ height: rowHeight }}>
        <Box sx={{ flex: titleFlex }} className={bold ? 'medium' : undefined}>
          <small>{titleIcon ?? title}</small>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box
            component="span"
            className={`${bold ? 'medium' : 'thin'} ${!smallFont ? 'md-title md-title-mobile' : ''} float-right`}
          >
            {displayValue()}
          </Box>
        </Box>
      </ListItem>
    )
  );
};
